import React, { useEffect } from "react"
import { useDispatch, useSelector } from "react-redux"
import {
  fetchScheduledObservations,
  selectScheduledObservationsByContext,
  selectScheduledObservationsByIds,
} from "../scheduled-observations/slice"
import { Link, useLocation, useNavigate } from "react-router-dom"
import { updateSearchParamsWithFilters } from "../filters/filters"
import Layout from "../../ui/Layout"
import NavMain from "../../app/NavMain"
import ScrollablePage from "../../ui/ScrollablePage"
import Paper from "../../ui/Paper"
import { Helmet } from "react-helmet-async"
import DataPageHeader from "../../ui/DataPageHeader"
import ScheduledObservationsTable from "../scheduled-observations/ScheduledObservationsTable"
import { parseScheduledObservationsFilters } from "../scheduled-observations/filters"
import SchedulesTable from "./SchedulesTable"
import {
  fetchSchedules,
  selectSchedulesByContext,
  selectSchedulesByIds,
} from "./slice"
import styled from "styled-components"
import { selectAstrositesById } from "../astrosites/slice"
import TableHeaderRow from "../../ui/TableHeaderRow"
import TableHeader from "../../ui/TableHeader"
import Table, { Cell, Row } from "../../ui/Table"
import Timestamp from "../../ui/Timestamp"
import AstrositeName from "../astrosites/AstrositeName"
import SunInfo from "../astrosites/SunInfo"
import MoonPhase from "./MoonPhase"
import ErrorMsg from "../../ui/ErrorMsg"
import { selectAdvancedMode } from "../settings/slice"
import LoadingSpinner, { Backdrop } from "../../ui/LoadingSpinner"
import PaperTitle, { PaperFooter, TitleText } from "../../ui/PaperTitle"
import PropTypes from "prop-types"

const PAGE_SIZE = 50 // Default initial and subsequent page size

const Container = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem 1rem;
  margin-bottom: 1rem;
`

/* ObservationsTable renders a given list of observations. */
const Schedules = () => {
  const dispatch = useDispatch()

  // we get any matching scheduled observations for this RSO
  const scheduledObservationsMatches = useSelector((state) =>
    selectScheduledObservationsByContext(state, "root")
  )
  // We get the full objects from the ids that match
  const scheduledObservations = useSelector((state) =>
    selectScheduledObservationsByIds(
      state,
      scheduledObservationsMatches?.ids || []
    )
  )

  // Parse parameters into filter object (which drives filters UI state)
  // Note: We are using the URL as the "state" here, and updates
  // perform a history replacement (so as not to mess up "back" button
  // behaviour too much - though we may want to apply a heuristic like
  // small changes replace history, big changes push history)
  const location = useLocation()
  const navigate = useNavigate()
  let filter = {}
  let urlParams = {}

  urlParams = new URLSearchParams(location.search)
  filter = parseScheduledObservationsFilters(urlParams)
  updateSearchParamsWithFilters(urlParams, filter)
  useEffect(() => {
    dispatch(
      fetchScheduledObservations({
        filter,
        page: { offset: 0, limit: PAGE_SIZE },
        context: ["root"],
      })
    )
  }, [urlParams.toString()])

  // How we apply a filter update. Filter state is entirely in the URL query parameters
  const setFilter = (filter = {}) => {
    const updUrlParams = updateSearchParamsWithFilters(urlParams, filter)
    navigate({
      pathname: location.pathname,
      search: `?${updUrlParams}`,
    })
  }

  return (
    <Layout>
      <NavMain key="nav-left" />
      <Helmet>
        <title>Schedules - Astrosite</title>
      </Helmet>
      {scheduledObservations.length === 0 &&
        scheduledObservationsMatches?._loadStatus === "loading" && (
          <Backdrop>
            <LoadingSpinner />
          </Backdrop>
        )}
      <ScrollablePage>
        <DataPageHeader title="Schedules" />
        <Paper>
          <RecentSchedules />
        </Paper>
        <ScheduledObservations
          scheduledObservations={scheduledObservations}
          scheduledObservationsMatches={scheduledObservationsMatches}
          filter={filter}
          setFilter={setFilter}
        />
      </ScrollablePage>
    </Layout>
  )
}

const ScheduledObservations = ({
  scheduledObservations = [],
  scheduledObservationsMatches = {},
  filter = {},
  setFilter,
}) => {
  const dispatch = useDispatch()

  // How many more observations are available to load?
  const loadableCount = Math.min(
    PAGE_SIZE,
    scheduledObservations &&
      scheduledObservations.length &&
      scheduledObservationsMatches &&
      scheduledObservationsMatches.page &&
      scheduledObservationsMatches.page.total_count
      ? scheduledObservationsMatches.page.total_count -
          scheduledObservations.length
      : 50
  )

  // Event handler for 'Show xxx more' button that was clicked
  const handleLoadMore = () => {
    const lastIndex = scheduledObservations && scheduledObservations.length

    const page = {
      offset: lastIndex,
      limit: loadableCount,
    }
    dispatch(fetchScheduledObservations({ filter, page, context: ["root"] }))
  }
  return (
    <Paper>
      <PaperTitle>
        <TitleText
          loadingState={scheduledObservationsMatches?._loadStatus || "idle"}
        >
          <span>scheduled observations </span>
          {scheduledObservations.length > 0 && (
            <span>
              {" "}
              - showing{" "}
              <strong>
                {scheduledObservationsMatches?.page?.end_index} of{" "}
                <strong className="count total_count">
                  {scheduledObservationsMatches?.page?.total_count}
                </strong>
              </strong>
            </span>
          )}{" "}
        </TitleText>
      </PaperTitle>

      {scheduledObservationsMatches?._loadStatus == "rejected" && (
        <ErrorMsg
          msg={"There is a problem fetching the scheduled observations"}
          showIcon={true}
        />
      )}
      <ScheduledObservationsTable
        scheduledObservations={scheduledObservations}
        filter={filter}
        setFilter={setFilter}
      />
      {scheduledObservations &&
        scheduledObservations.length == 0 &&
        scheduledObservations?._loadStatus == "idle" && (
          <p>There are no scheduled observations matching your criteria.</p>
        )}
      <PaperFooter>
        {scheduledObservations.length > 0 &&
          scheduledObservations.length !==
            scheduledObservationsMatches?.page?.total_count && (
            <>
              <button
                onClick={handleLoadMore}
                disabled={
                  scheduledObservationsMatches?._loadStatus == "loading"
                }
              >
                {scheduledObservationsMatches?._loadStatus == "loading"
                  ? "Loading..."
                  : `Show ${loadableCount} more`}
              </button>
            </>
          )}
      </PaperFooter>
    </Paper>
  )
}

ScheduledObservations.propTypes = {
  scheduledObservations: PropTypes.array,
  scheduledObservationsMatches: PropTypes.object,
  filter: PropTypes.object,
  setFilter: PropTypes.func,
}

// Renders a table of the observations
const RecentSchedules = () => {
  const dispatch = useDispatch()
  const advancedMode = useSelector(selectAdvancedMode)

  const astrositesById = useSelector(selectAstrositesById)
  // we get any matching schedules
  const scheduleMatches = useSelector((state) =>
    selectSchedulesByContext(state, "schedules/observations")
  )
  // We get the full objects from the ids that match
  const schedules = useSelector((state) =>
    selectSchedulesByIds(state, scheduleMatches?.ids || [])
  )

  useEffect(() => {
    dispatch(
      fetchSchedules({
        filter: { sort_by: "created", sort_order: "DESC" },
        page: { offset: 0, limit: 7 },
        context: ["schedules/observations"],
      })
    )
  }, [])

  const latestScheduleByAstrosite = Object.keys(astrositesById).map(
    (astrosite) => {
      return schedules.find((s) => s.astrosite === astrosite)
    }
  )

  const remainingSchedules = schedules.filter(
    (s) => !latestScheduleByAstrosite.includes(s)
  )

  return (
    <Container>
      <Paper>
        <PaperTitle>
          <TitleText>Current schedules</TitleText>
        </PaperTitle>
        {scheduleMatches?._loadStatus == "rejected" && (
          <ErrorMsg
            msg={"There was a problem fetching the schedules"}
            showIcon={true}
          />
        )}
        <Table>
          <TableHeaderRow sticky>
            <TableHeader>Date & Time</TableHeader>
            <TableHeader>Astrosite</TableHeader>
            <TableHeader>Sun info</TableHeader>
            <TableHeader>Moon info</TableHeader>
            <TableHeader />
          </TableHeaderRow>
          {latestScheduleByAstrosite && (
            <tbody>
              {latestScheduleByAstrosite.map(
                (schedule) =>
                  schedule && (
                    <Row key={schedule.id} zebra>
                      <Cell>
                        <Timestamp utc={advancedMode} columnMode={true}>
                          {schedule.created}
                        </Timestamp>
                      </Cell>
                      <Cell>
                        <AstrositeName astrositeId={schedule.astrosite} />
                      </Cell>
                      <Cell>
                        {schedule.sun && <SunInfo {...schedule.sun} />}
                      </Cell>
                      <Cell>
                        {schedule.moon_phase && (
                          <MoonPhase moon_phase={schedule.moon_phase} />
                        )}
                      </Cell>
                      <Cell>
                        <Link to={`/schedules/${schedule.id}`}>Detail</Link>
                      </Cell>
                    </Row>
                  )
              )}
            </tbody>
          )}
        </Table>
      </Paper>
      <Paper>
        <PaperTitle>
          <TitleText>Current schedules</TitleText>
        </PaperTitle>
        <SchedulesTable schedules={remainingSchedules || []} />
        {remainingSchedules.length != 0 &&
          scheduleMatches?.page?.total_count > schedules.length && (
            <PaperFooter>
              <span>
                showing {remainingSchedules.length} of{" "}
                {scheduleMatches?.page?.total_count}{" "}
                <Link to={`/schedules/all`}>view all</Link>
              </span>
            </PaperFooter>
          )}
      </Paper>
    </Container>
  )
}

export default Schedules
